import { normalizeGrassType } from "@/lib/lawn-plan-engine/normalization"

export const COMMUNITY_DISCOVERY_SOURCES = [
  "identify_result",
  "diagnose_result",
  "saved_lawn_success",
  "saved_plan_success",
  "header_explore",
  "mobile_browse",
  "footer",
  "grass_guide",
] as const

export type CommunityDiscoverySource = typeof COMMUNITY_DISCOVERY_SOURCES[number]

export const COMMUNITY_DISCOVERY_ATTRIBUTION_COOKIE = "community_discovery_attribution"
export const COMMUNITY_DISCOVERY_ATTRIBUTION_TTL_MS = 30 * 60 * 1000

export interface CommunityDiscoveryAttribution {
  source: CommunityDiscoverySource
  grassType?: string
  ts: number
}

export interface CommunityDiscoveryContext {
  source: CommunityDiscoverySource
  mode?: "identify" | "diagnose"
  entryPath?: string
  grassType?: string | null
  problem?: string | null
  planType?: string
  outcome?: string
}

export function parseCommunityDiscoverySource(
  value: string | null | undefined,
): CommunityDiscoverySource | undefined {
  return COMMUNITY_DISCOVERY_SOURCES.find((source) => source === value)
}

export function resolveCommunityGrassType(raw?: string | null): string | null {
  const resolution = normalizeGrassType(raw)
  return resolution.isKnown ? resolution.canonical : null
}

export function parseCommunityDiscoveryAttribution(
  value: string | null | undefined,
  now = Date.now(),
): CommunityDiscoveryAttribution | null {
  if (!value) return null

  try {
    const parsed = JSON.parse(decodeURIComponent(value)) as Partial<CommunityDiscoveryAttribution>
    const source = parseCommunityDiscoverySource(parsed.source)
    const grassType = resolveCommunityGrassType(parsed.grassType)
    const timestamp = parsed.ts
    const timestampIsValid =
      typeof timestamp === "number" &&
      Number.isFinite(timestamp) &&
      timestamp <= now + 60_000 &&
      now - timestamp <= COMMUNITY_DISCOVERY_ATTRIBUTION_TTL_MS

    if (!source || !timestampIsValid) return null

    return {
      source,
      ...(grassType ? { grassType } : {}),
      ts: timestamp,
    }
  } catch {
    return null
  }
}

export function stashCommunityDiscoveryAttribution(
  context: CommunityDiscoveryContext,
  now = Date.now(),
): void {
  if (typeof document === "undefined" || typeof window === "undefined") return

  const source = parseCommunityDiscoverySource(context.source)
  if (!source) return

  const grassType = resolveCommunityGrassType(context.grassType)
  const attribution: CommunityDiscoveryAttribution = {
    source,
    ...(grassType ? { grassType } : {}),
    ts: now,
  }

  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : ""
    document.cookie =
      `${COMMUNITY_DISCOVERY_ATTRIBUTION_COOKIE}=${encodeURIComponent(JSON.stringify(attribution))}` +
      `; path=/community; max-age=${Math.floor(COMMUNITY_DISCOVERY_ATTRIBUTION_TTL_MS / 1000)}; SameSite=Lax${secure}`
  } catch {
    // Attribution is best-effort and must never block navigation.
  }
}

export function resolveCommunitySubmitAttribution({
  source,
  grassType,
  attributionCookie,
  now = Date.now(),
}: {
  source?: string | null
  grassType?: string | null
  attributionCookie?: string | null
  now?: number
}): {
  source?: CommunityDiscoverySource
  grassType: string | null
} {
  const querySource = parseCommunityDiscoverySource(source)
  const queryGrassType = resolveCommunityGrassType(grassType)

  // A direct share CTA carries its own source. Do not mix that fresh intent
  // with grass attribution from an older gallery visit.
  if (querySource) {
    return { source: querySource, grassType: queryGrassType }
  }

  const stored = parseCommunityDiscoveryAttribution(attributionCookie, now)
  return {
    source: stored?.source,
    grassType: queryGrassType ?? stored?.grassType ?? null,
  }
}

export function buildCommunityGalleryHref(grassType?: string | null): string {
  const canonicalGrassType = resolveCommunityGrassType(grassType)
  return canonicalGrassType
    ? `/community?grassType=${encodeURIComponent(canonicalGrassType)}`
    : "/community"
}

export function buildCommunitySubmitHref({
  grassType,
  source,
}: {
  grassType?: string | null
  source: CommunityDiscoverySource
}): string {
  const params = new URLSearchParams({ source })
  const canonicalGrassType = resolveCommunityGrassType(grassType)
  if (canonicalGrassType) params.set("grassType", canonicalGrassType)

  return `/community/submit?${params.toString()}`
}
